import Minze from 'minze'
import {
  IFlowContainer,
  IFlowCore,
  IFlowCoreConfig,
  IFlowSubscriber,
  IFlowTransition,
} from './FlowInterfaces'

export class FlowCore implements IFlowCore {
  private currentStep = 0
  private subscribers: IFlowSubscriber[] = []
  private transitions: IFlowTransition[] = []
  private container: IFlowContainer | null = null
  private config: IFlowCoreConfig = {}
  private data: any = {}
  private calcProgress = (a: any, b: any, c: any) => 0
  private saveTimeout: NodeJS.Timeout | null = null
  saveState: 'not_saved' | 'saving' | 'saved' = 'not_saved'

  // Flag to check if the flow has been initialized
  private initialized = false

  constructor(config: IFlowCoreConfig) {
    this.config = { ...config }
    this.transitions = config.transitions || []
    this.container = config.flowContainer || null

    if (config.initialStep) {
      this.currentStep = this.getStepIndex(config.initialStep)
    }

    if (config.calcProgress && typeof config.calcProgress === 'function') {
      this.calcProgress = config.calcProgress
    }
  }

  public get flowGroup() {
    return this?.container?.flowGroup ?? ''
  }

  public getData(): any {
    return this.data
  }

  /**
   * iterates through the transitions and returns the highest index
   * So we go to the furthest step that has been completed
   */
  private getFurthestStepIndex(): number {
    let furthest = 0
    this.transitions.forEach((transition, idx) => {
      if (this.data[transition.stepName]) {
        // If we are in EventFlow, and have date up to step 6, we need to set furthest to 5
        // This is a special case for the event flow where we want to send back to the "representations Recap"
        if (idx === 6 && this.config.eventType === 'event') {
          furthest = 5
        } else if (idx > 1) {
          furthest = idx
        } else {
          furthest = 1
        }
      }
    })

    // If we load draft festival and we are modifying an existing event, send to the final recap step
    if (this.config.eventType === 'festival' && this.data.control_modifyingExistingEvent) {
      // Find the index for 'finalEventRecap'
      const finalRecapIndex = this.getStepIndex('finalEventRecap')
      if (finalRecapIndex > furthest) {
        furthest = finalRecapIndex
      }
    }

    return furthest
  }

  public async initialize(): Promise<void> {
    await this.load()
    // If draft data was loaded (this.data is not empty), set currentStep to the furthest step
    if (this.data && Object.keys(this.data).length > 0) {
      this.currentStep = this.getFurthestStepIndex()
    }
    const transitionStep = this.getCurrentTransitionStep()
    if (this.container?.onContainerUpdate) {
      await this.container.onContainerUpdate(this.data, transitionStep)
    }
    const subscriber = this.getSubscriber(transitionStep.stepName)
    if (subscriber) {
      await subscriber.deserialize?.(this.getStepData(transitionStep.stepName), this.data)
    }
    this.initialized = true
  }

  public subscribe(subscriber: IFlowSubscriber) {
    if (subscriber?.flowGroup !== this.flowGroup) {
      return
    }
    this.subscribers.push(subscriber)
    if (this.initialized) {
      const currentStep = this.getCurrentTransitionStep()
      if (currentStep) {
        const stepData = this.getStepData(currentStep.stepName)
        subscriber.deserialize?.(stepData, this.data)
      }
    }
  }

  public unsubscribe(subscriber: IFlowSubscriber) {
    this.subscribers = this.subscribers.filter(s => s !== subscriber)
  }

  public getCurrentTransitionStep(): IFlowTransition {
    return this.transitions[this.currentStep]
  }

  private getStepData(stepName: string): any {
    return this.data[stepName]
  }

  private getStepIndex(stepName: string): number {
    return this.transitions.findIndex(t => t.stepName === stepName)
  }

  private getSubscriber(stepName: string): IFlowSubscriber | undefined {
    return this.subscribers.find(s => s.flowStep === stepName)
  }

  public async setStepData(stepName: string, data: any): Promise<any> {
    this.data[stepName] = data
  }

  public getProgress(): number {
    const progress = this.calcProgress(this.data, this.getCurrentTransitionStep(), this.transitions)
    if (progress > 100) return 100
    if (progress < 0) return 0
    return progress
  }

  /**
   * Go to the next step in the flow
   */
  public async next(): Promise<void> {
    const transitionStep = this.getCurrentTransitionStep()

    const subscriber = this.getSubscriber(transitionStep.stepName)
    if (!subscriber) return

    this.saveState = 'saving'
    Minze.cast('lvc:pro-space-save', 'saving')

    let isValid = true
    if (subscriber.validate && typeof subscriber.validate === 'function') {
      isValid = await subscriber.validate()
    }
    if (!isValid) {
      this.saveState = 'saved'
      Minze.cast('lvc:pro-space-save', 'saved')
      return
    }
    let stepData = subscriber.serialize ? await subscriber.serialize() : null
    this.data[transitionStep.stepName] = stepData
    await this.save(true)
    const nextStep = await transitionStep.transitionNext(this.data)
    this.currentStep = this.getStepIndex(nextStep)
    const nextTransitionStep = this.getCurrentTransitionStep()
    const nextSubscriber = this.getSubscriber(nextTransitionStep.stepName)
    if (this.container?.onContainerUpdate) {
      await this.container.onContainerUpdate(this.data, nextTransitionStep)
    }
    if (nextSubscriber) {
      await nextSubscriber.deserialize?.(this.getStepData(nextTransitionStep.stepName), this.data)
    }
    this.saveState = 'saved'
    Minze.cast('lvc:pro-space-save', 'saved')
  }

  /**
   * Go to the previous step in the flow
   */
  public async prev(): Promise<void> {
    const transitionStep = this.getCurrentTransitionStep()
    const subscriber = this.getSubscriber(transitionStep.stepName)
    if (!subscriber) return

    this.saveState = 'saving'
    Minze.cast('lvc:pro-space-save', 'saving')

    let stepData = subscriber.serialize ? await subscriber.serialize() : null
    this.data[transitionStep.stepName] = stepData
    await this.save(true)
    const prevStep = await transitionStep.transitionPrev(this.data)
    this.currentStep = this.getStepIndex(prevStep)
    const prevTransitionStep = this.getCurrentTransitionStep()
    const prevSubscriber = this.getSubscriber(prevTransitionStep.stepName)
    if (this.container?.onContainerUpdate) {
      await this.container.onContainerUpdate(this.data, prevTransitionStep)
    }
    if (prevSubscriber) {
      await prevSubscriber.deserialize?.(this.getStepData(prevTransitionStep.stepName), this.data)
    }
    this.saveState = 'saved'
    Minze.cast('lvc:pro-space-save', 'saved')
  }

  /**
   * Go to a specific step in the flow
   */
  public async goTo(stepName: string): Promise<void> {
    const transitionStep = this.getCurrentTransitionStep()
    const subscriber = this.getSubscriber(transitionStep.stepName)
    if (!subscriber) return

    this.saveState = 'saving'
    Minze.cast('lvc:pro-space-save', 'saving')

    let isValid = true
    if (subscriber.validate && typeof subscriber.validate === 'function') {
      isValid = await subscriber.validate()
    }
    if (!isValid) {
      // A failing validation must block navigation, not just flip the save
      // state — otherwise an invalid step can be skipped past (PRO-48).
      this.saveState = 'saved'
      Minze.cast('lvc:pro-space-save', 'saved')
      return
    }
    let stepData = subscriber.serialize ? await subscriber.serialize() : null
    this.data[transitionStep.stepName] = stepData
    await this.save(true)
    this.currentStep = this.getStepIndex(stepName)
    const nextTransitionStep = this.getCurrentTransitionStep()
    const nextSubscriber = this.getSubscriber(nextTransitionStep.stepName)
    if (this.container?.onContainerUpdate) {
      await this.container.onContainerUpdate(this.data, nextTransitionStep)
    }
    if (nextSubscriber) {
      await nextSubscriber.deserialize?.(this.getStepData(nextTransitionStep.stepName), this.data)
    }
    this.saveState = 'saved'
    Minze.cast('lvc:pro-space-save', 'saved')
  }

  // function when you submit the event
  public async commit(): Promise<void> {
    this.saveState = 'saving'
    Minze.cast('lvc:pro-space-save', 'saving')
    await this.save(true)
    if (this.config?.onCommit) {
      await this.config.onCommit(this.data, this)
    }
    this.saveState = 'saved'
    Minze.cast('lvc:pro-space-save', 'saved')
  }

  public async load(): Promise<void> {
    if (this.config?.onLoad) {
      this.data = await this.config.onLoad(this)
    }
  }

  // function that gets called every time a change is made to a form in the flow
  private async scheduleAutoSave() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
    this.saveTimeout = setTimeout(async () => {
      await this.save(true)
    }, 5000)
  }

  // function to save the data
  public async save(forceImmediateSave = false): Promise<void> {
    if (!this.config || typeof this.config.onSave !== 'function') {
      return
    }
    const transitionStep = this.getCurrentTransitionStep()
    const subscriber = this.getSubscriber(transitionStep.stepName)
    if (!subscriber) {
      return
    }
    let stepData = subscriber.serialize ? await subscriber.serialize(forceImmediateSave) : null
    if (!stepData) {
      // Do not override previously saved data if step data is empty.
      return
    }
    this.data[transitionStep.stepName] = stepData

    if (forceImmediateSave) {
      // Set state to saving and then perform save
      // this.saveState = 'saving'
      // Minze.cast('lvc:pro-space-save', 'saving')
      await this.config.onSave(this.data, this)
      this.saveState = 'saved'
      // Broadcast a 'saved' state so the UI can update the last saved time
      //Minze.cast('lvc:pro-space-save', 'saved')
      return
    }

    this.scheduleAutoSave()
  }

  public getSaveState(): 'not_saved' | 'saving' | 'saved' {
    return this.saveState
  }

  public loadDraft(draftData: any) {
    if (!draftData) return
    this.data = draftData.draftData || {}
    // Cast the lastModified time so that the footer can catch it and update the UI
    Minze.cast('lvc:pro-space-draft-loaded', draftData.lastModified)
  }
}
